import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { Loader2, LogOut, Users, UtensilsCrossed, ShoppingBag } from 'lucide-react';

const AdminDashboard = () => {
  const [reservations, setReservations] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');
  const navigate = useNavigate();

  const fetchAdminData = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

      const [resResponse, subResponse, ordResponse] = await Promise.all([
        fetch(`${backendUrl}/api/reservations`, { headers }),
        fetch(`${backendUrl}/api/newsletter`, { headers }),
        fetch(`${backendUrl}/api/orders`, { headers })
      ]);

      if (resResponse.status === 401 || subResponse.status === 401 || ordResponse.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
        return;
      }

      const resData = await resResponse.json();
      const subData = await subResponse.json();
      const ordData = await ordResponse.json();

      setReservations(Array.isArray(resData) ? resData : []);
      setSubscribers(Array.isArray(subData) ? subData : []);
      setOrders(Array.isArray(ordData) ? ordData : []);
    } catch (error) {
      toast.error('Hiba az adatok betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleStatusChange = async (orderId, newStatus) => {
    const token = localStorage.getItem('adminToken');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

    try {
      const response = await fetch(`${backendUrl}/api/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Nem sikerült frissíteni a státuszt');

      toast.success('Státusz frissítve!');
      // Refresh data
      fetchAdminData();
    } catch (error) {
      toast.error('Hiba történt: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Új': return 'text-blue-500 font-bold';
      case 'Készítés alatt': return 'text-orange-500 font-bold';
      case 'Kiszállítva': return 'text-purple-500 font-bold';
      case 'Teljesítve': return 'text-green-500 font-bold';
      default: return 'text-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="container mx-auto max-w-6xl">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-serif font-bold text-primary">Admin Vezérlőpult</h1>
          <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            <LogOut className="w-4 h-4" />
            Kilépés
          </Button>
        </div>

        <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
          <Button 
            variant={activeTab === 'menu' ? 'default' : 'outline'}
            onClick={() => navigate('/admin/menu')}
            className={`flex items-center gap-2 ${activeTab === 'menu' ? 'bg-primary text-primary-foreground' : 'text-primary border-primary hover:bg-primary/10'}`}
          >
            <ShoppingBag className="w-4 h-4" />
            Étlap Kezelése
          </Button>
          <Button 
            variant={activeTab === 'orders' ? 'default' : 'outline'}
            onClick={() => setActiveTab('orders')}
            className={`flex items-center gap-2 ${activeTab === 'orders' ? 'bg-primary text-primary-foreground' : 'text-primary border-primary hover:bg-primary/10'}`}
          >
            <ShoppingBag className="w-4 h-4" />
            Rendelések ({orders.length})
          </Button>
          <Button 
            variant={activeTab === 'reservations' ? 'default' : 'outline'}
            onClick={() => setActiveTab('reservations')}
            className={`flex items-center gap-2 ${activeTab === 'reservations' ? 'bg-primary text-primary-foreground' : 'text-primary border-primary hover:bg-primary/10'}`}
          >
            <UtensilsCrossed className="w-4 h-4" />
            Foglalások ({reservations.length})
          </Button>
          <Button 
            variant={activeTab === 'newsletter' ? 'default' : 'outline'}
            onClick={() => setActiveTab('newsletter')}
            className={`flex items-center gap-2 ${activeTab === 'newsletter' ? 'bg-primary text-primary-foreground' : 'text-primary border-primary hover:bg-primary/10'}`}
          >
            <Users className="w-4 h-4" />
            Hírlevél ({subscribers.length})
          </Button>
        </div>

        {activeTab === 'orders' && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Beérkező Rendelések</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-secondary/50">
                      <TableHead className="text-muted-foreground whitespace-nowrap">Dátum</TableHead>
                      <TableHead className="text-muted-foreground min-w-[150px]">Név & Telefon</TableHead>
                      <TableHead className="text-muted-foreground min-w-[200px]">Cím</TableHead>
                      <TableHead className="text-muted-foreground min-w-[250px]">Rendelés</TableHead>
                      <TableHead className="text-muted-foreground whitespace-nowrap">Fizetés</TableHead>
                      <TableHead className="text-muted-foreground min-w-[160px]">Státusz</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Nincs megjeleníthető rendelés
                        </TableCell>
                      </TableRow>
                    ) : (
                      orders.map((order) => (
                        <TableRow key={order.id} className="border-border hover:bg-secondary/50">
                          <TableCell className="font-medium text-foreground">
                            {format(new Date(order.created_at), 'yyyy. MM. dd. HH:mm', { locale: hu })}
                          </TableCell>
                          <TableCell className="text-foreground">
                            <div className="font-semibold">{order.name}</div>
                            <div className="text-sm text-muted-foreground">{order.phone}</div>
                          </TableCell>
                          <TableCell className="text-foreground">{order.address}</TableCell>
                          <TableCell className="text-foreground whitespace-pre-wrap">{order.content}</TableCell>
                          <TableCell className="text-foreground">{order.payment_method}</TableCell>
                          <TableCell className="text-foreground">
                            <Select 
                              defaultValue={order.status} 
                              onValueChange={(value) => handleStatusChange(order.id, value)}
                            >
                              <SelectTrigger className={`w-[140px] ${getStatusColor(order.status)} border-input bg-background`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-popover text-popover-foreground border-border">
                                <SelectItem value="Új" className="text-blue-500 font-semibold cursor-pointer hover:bg-accent">Új</SelectItem>
                                <SelectItem value="Készítés alatt" className="text-orange-500 font-semibold cursor-pointer hover:bg-accent">Készítés alatt</SelectItem>
                                <SelectItem value="Kiszállítva" className="text-purple-500 font-semibold cursor-pointer hover:bg-accent">Kiszállítva</SelectItem>
                                <SelectItem value="Teljesítve" className="text-green-500 font-semibold cursor-pointer hover:bg-accent">Teljesítve</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'reservations' && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Asztalfoglalások</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-secondary/50">
                      <TableHead className="text-muted-foreground whitespace-nowrap">Dátum</TableHead>
                      <TableHead className="text-muted-foreground whitespace-nowrap">Idő</TableHead>
                      <TableHead className="text-muted-foreground min-w-[150px]">Név</TableHead>
                      <TableHead className="text-muted-foreground min-w-[150px]">Telefon</TableHead>
                      <TableHead className="text-muted-foreground whitespace-nowrap">Vendégek</TableHead>
                      <TableHead className="text-muted-foreground min-w-[200px]">Email</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reservations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Nincs megjeleníthető foglalás
                        </TableCell>
                      </TableRow>
                    ) : (
                      reservations.map((res) => (
                        <TableRow key={res.id} className="border-border hover:bg-secondary/50">
                          <TableCell className="font-medium text-foreground">
                            {format(new Date(res.date), 'yyyy. MM. dd.', { locale: hu })}
                          </TableCell>
                          <TableCell className="text-foreground">{res.time}</TableCell>
                          <TableCell className="text-foreground">{res.name}</TableCell>
                          <TableCell className="text-foreground">{res.phone}</TableCell>
                          <TableCell className="text-foreground">{res.guests} fő</TableCell>
                          <TableCell className="text-foreground">{res.email || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'newsletter' && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Hírlevél Feliratkozók</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-secondary/50">
                      <TableHead className="text-muted-foreground">Email cím</TableHead>
                      <TableHead className="text-muted-foreground w-[200px]">Feliratkozás ideje</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscribers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center py-8 text-muted-foreground">
                          Nincs megjeleníthető feliratkozó
                        </TableCell>
                      </TableRow>
                    ) : (
                      subscribers.map((sub) => (
                        <TableRow key={sub.id} className="border-border hover:bg-secondary/50">
                          <TableCell className="font-medium text-foreground">{sub.email}</TableCell>
                          <TableCell className="text-foreground">
                            {format(new Date(sub.subscribed_at), 'yyyy. MM. dd. HH:mm', { locale: hu })}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
